import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';

import { SecondaryButton } from '@common/button';
import { useBookingContext } from '@common/context/BookingContext';
import InputField from '@common/fields/InputField';
import { Heading4, Text } from '@common/Typography';
import useFeatureFlag from '@common/utils/hooks/useFeatureFlag';
import useQueryParams from '@common/utils/hooks/useQueryParams';
import { sortBy } from '@common/utils/sortBy';
import { ListingsQuery } from '@typings/api';

import Section from '../_common/Section';
import SectionHeader from '../_common/SectionHeader';
import { MainFormFields } from '../BookingFlow';
import { LISTINGS } from '../queries';

import { ListingsSkeleton } from './ListingsSkeleton';

export function Listings() {
  const { t } = useTranslation();
  const { id: contextId } = useQueryParams();
  const [{ customerFormSubmissionCount }] = useBookingContext();
  const { batch, change } = useForm();
  const isListingsEnabled = useFeatureFlag('steps.listings.enabled');

  const {
    data,
    error,
    loading: listingsLoading,
  } = useQuery<ListingsQuery>(LISTINGS, {
    variables: {
      contextId,
    },
    skip: !isListingsEnabled || !contextId || customerFormSubmissionCount === 0,
    fetchPolicy: 'network-only',
  });
  const listings = useMemo(() => sortBy(data?.listings ?? [], 'title'), [data]);

  const handleChange = useCallback(
    (listingId: string | null) => {
      batch(() => {
        change(MainFormFields.LISTING, listingId);
        change(MainFormFields.MEETING_TYPE, undefined);
        change(MainFormFields.OFFICES, undefined);
        change(MainFormFields.CONTACT, undefined);
        change(MainFormFields.TIME_SLOT, undefined);
        change(MainFormFields.SUBJECT, undefined);
      });
    },
    [batch, change],
  );

  useEffect(() => {
    if (data?.listings?.length === 1) {
      handleChange(data.listings[0].id);
    }

    if (data?.listings?.length === 0) {
      handleChange(null);
    }
    // Explicitly using data instead of listings const here,
    // because listings defaults to empty array, so handleChange(null) would be called initially
  }, [data, handleChange]);

  if (listingsLoading) {
    return <ListingsSkeleton />;
  }

  if (error) {
    return (
      <Section>
        <SectionHeader>{t('main_form.listing.question')}</SectionHeader>
        <p>{t('main_form.no_actions_possible')}</p>
      </Section>
    );
  }

  if (listings.length <= 1) {
    return null;
  }

  return (
    <Section>
      <SectionHeader>{t('main_form.listing.question')}</SectionHeader>
      <InputField name={MainFormFields.LISTING}>
        {(input) =>
          listings.map(({ id, location, title }) => {
            const isSelected = input.value === id;

            return (
              <div
                className={`border-t border-r border-l border-gray-300 last:border-b ${
                  isSelected ? 'bg-gray-300' : 'bg-gray-100'
                }`}
                key={id}
              >
                <div
                  className={`flex flex-col justify-between py-5 px-5 space-y-2 lg:flex-row lg:items-center lg:space-x-4 lg:space-y-0 ${
                    isSelected ? 'cursor-default' : 'cursor-pointer'
                  }`}
                >
                  <div className="flex flex-1 lg:items-center">
                    <div className="flex flex-1 flex-col justify-center">
                      <Heading4>{title}</Heading4>
                      <Text>{location?.full_address}</Text>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <SecondaryButton
                      ariaLabel={title}
                      disabled={isSelected}
                      onClick={() => handleChange(id)}
                      type="button"
                    >
                      {isSelected ? t('button.chosen') : t('button.choose')}
                    </SecondaryButton>
                  </div>
                </div>
              </div>
            );
          })
        }
      </InputField>
    </Section>
  );
}
